import { useEffect, useRef } from "react";
import { cn } from "@/shared/utils/cn";
import { formatPrice } from "@/shared/utils/helpers";
import SplitPaymentLedger from "./SplitPaymentLedger";
import { CartItem } from "@/shared/utils/interfaces";
import useSplitPaymentHook from "@/shared/hooks/useSplitPaymentHook";
import Icon from "../Icon/Icon";
import Center from "../Base/Center";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import {
  useReadSplitPaymentOrdersState,
  useSetSplitPaymentOrdersState,
} from "@/shared/atoms/useSplitPaymentV2Atom";

const LedgerTabs = ({
  handleBack,
  handleNext,
  active,
  paymentLedgers,
  onChangeActive,
}) => {
  return (
    <div className="flex justify-between my-6">
      <Center
        className="bg-dark/10 px-3 md:px-6 md:py-4 rounded-2xl cursor-pointer"
        onClick={handleBack}
      >
        <Icon className="size-7 md:size-10" variants="arrowLeft" />
      </Center>
      {paymentLedgers.map((ledger, index) => (
        <div
          className="flex items-center gap-1 md:gap-2 cursor-pointer"
          key={ledger.item_id}
        >
          <div
            key={ledger.item_id}
            className={cn(
              "mb-2 p-2 md:py-4 md:px-6 rounded-2xl font-semibold",
              ledger.is_paid
                ? "bg-success/10 text-success"
                : "bg-dark/10 text-dark",
              active === index && "border-2 border-primary"
            )}
            onClick={() => onChangeActive(index)}
          >
            {formatPrice(ledger.payment_amount)}
          </div>
        </div>
      ))}

      <Center
        className="bg-dark/10 px-3 md:px-6 md:py-4 rounded-2xl cursor-pointer"
        onClick={handleNext}
      >
        <Icon className="size-7 md:size-10 rotate-180" variants="arrowLeft" />
      </Center>
    </div>
  );
};

type SplitPaymentListWithTabsProps = {
  splitPaymentCart: CartItem[];
};

const SplitPaymentListWithTabs = ({
  splitPaymentCart,
}: SplitPaymentListWithTabsProps) => {
  const { paymentLedgers, activeIndex } = useReadSplitPaymentOrdersState();
  const setSplitPaymentState = useSetSplitPaymentOrdersState();
  const { handleNext, handleBack } = useSplitPaymentHook(splitPaymentCart);
  const swiperRef = useRef<any>(null);

  useEffect(() => {
    swiperRef.current?.slideTo(activeIndex);
  }, [activeIndex]);

  return (
    <div>
      <LedgerTabs
        handleBack={handleBack}
        handleNext={handleNext}
        active={activeIndex}
        paymentLedgers={paymentLedgers}
        onChangeActive={(step: number) =>
          setSplitPaymentState({ activeIndex: step })
        }
      />
      <Swiper
        spaceBetween={50}
        slidesPerView={1}
        initialSlide={activeIndex}
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        onSlideChange={(e) =>
          setSplitPaymentState({ activeIndex: e.activeIndex })
        }
      >
        {paymentLedgers?.map((ledger, index) => (
          <SwiperSlide>
            <SplitPaymentLedger
              splitPaymentCart={splitPaymentCart}
              key={ledger.item_id}
              ledger={ledger}
              index={index}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default SplitPaymentListWithTabs;
